import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, FileDown, ArrowRight, Lock, PlayCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WebinarWatch = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | invalid

  useEffect(() => {
    axios.get(`${API}/public/webinar/watch/${token}`)
      .then((r) => { setData(r.data); setState('ok'); document.title = `${r.data.title} | Care Tech Compare`; })
      .catch(() => setState('invalid'));
  }, [token]);

  if (state === 'loading') return (
    <div className="flex justify-center py-32" data-testid="watch-loading"><Loader2 className="w-6 h-6 animate-spin text-[#00A79D]" /></div>
  );

  if (state === 'invalid') return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="watch-invalid">
      <Lock className="w-10 h-10 text-gray-300 mx-auto mb-4" />
      <h1 className="font-heading text-2xl font-bold text-gray-900">This link isn't valid</h1>
      <p className="text-gray-600 mt-2">Sign up to watch the recording and we'll take you straight to it.</p>
      <Link to="/webinars" className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md mt-6 transition-colors" data-testid="watch-signup-cta">
        Sign up to watch <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );

  const pdfUrl = data.slides_pdf?.startsWith('http') ? data.slides_pdf : `${data.slides_pdf}`;

  return (
    <div data-testid="page-webinar-watch">
      <section className="bg-[#1A3A5F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2fd0c6] bg-white/10 px-3 py-1 rounded-full mb-4"><PlayCircle className="w-3.5 h-3.5" /> On-demand recording</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-white leading-tight" data-testid="watch-title">{data.title}</h1>
          {data.name && <p className="text-gray-300 mt-3">Welcome{data.name ? `, ${data.name.split(' ')[0]}` : ''} - press play whenever you're ready.</p>}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-xl overflow-hidden border border-[#E5E7EB] bg-black shadow-sm" style={{ position: 'relative', paddingTop: '56.25%' }} data-testid="watch-video">
          <iframe
            src={data.embed_url}
            title={data.title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
          />
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 justify-between bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-5">
          <div>
            <p className="font-heading font-bold text-gray-900">Get the slides</p>
            <p className="text-sm text-gray-600">Download the full presentation to keep and share with your team.</p>
          </div>
          <a href={pdfUrl} download target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-5 py-3 rounded-md transition-colors shrink-0" data-testid="watch-pdf-download">
            <FileDown className="w-4 h-4" /> Download the slides (PDF)
          </a>
        </div>

        <div className="mt-8 bg-white border border-[#E5E7EB] rounded-xl p-6 text-center">
          <h2 className="font-heading text-xl font-bold text-gray-900">Ready to get listed?</h2>
          <p className="text-gray-600 mt-2 max-w-xl mx-auto">Claim your listing so care providers can find and shortlist you when they're comparing technology.</p>
          <Link to="/get-started" className="inline-flex items-center gap-1.5 bg-[#1A3A5F] hover:bg-[#0f2a47] text-white font-semibold px-6 py-3 rounded-md mt-5 transition-colors" data-testid="watch-claim-cta">
            Claim your listing <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WebinarWatch;
